import getPrioritizedBedbankAmenities from 'lib/offer/getPrioritizedBedbankAmenities'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import type { OfferDetailResult } from 'tripPlanner/api/types'
import type { DrawerHotel } from './types'
import { mapImages } from './mapToDrawerHotel'

export function mapOfferDetailInclusions(detail: OfferDetailResult): NonNullable<DrawerHotel['inclusions']> {
  return detail.inclusions?.map((inc, index) => ({
    id: `${detail.offerId}-inc-${index}`,
    description: inc.text,
    symbol: (inc.icon ?? undefined) as App.OfferFeatureSymbol | undefined,
    isHighlighted: inc.isHighlighted,
  })) ?? []
}

export function mapOfferDetailAmenities(detail: OfferDetailResult): DrawerHotel['amenities'] {
  return detail.amenities?.map((amenity) => ({
    name: amenity.name,
    icon: amenity.icon,
  })) ?? []
}

export function mergeDrawerHotelWithOfferDetail(
  hotel: DrawerHotel,
  detail: OfferDetailResult,
): DrawerHotel {
  return {
    ...hotel,
    rating: detail.rating ?? hotel.rating,
    reviewsTotal: detail.reviewsTotal ?? hotel.reviewsTotal,
    inclusions: mapOfferDetailInclusions(detail),
    amenities: mapOfferDetailAmenities(detail),
  }
}

export function mapOfferToDrawerHotelTileFeatures(
  offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary,
): Pick<DrawerHotel, 'inclusions' | 'tileAmenities'> {
  if (isBedbankOffer(offer)) {
    return {
      inclusions: [],
      tileAmenities: getPrioritizedBedbankAmenities({
        popularFacilities: offer.popularFacilities,
      }),
    }
  }

  if (offer.tileInclusions?.length) {
    return {
      inclusions: offer.tileInclusions,
      tileAmenities: [],
    }
  }

  const lowestPackageRateInclusions = offer.lowestPricePackage?.inclusions?.filter(
    (inclusion) => inclusion.displayContext === 'Rate',
  ) ?? []

  if (lowestPackageRateInclusions.length) {
    return {
      inclusions: lowestPackageRateInclusions,
      tileAmenities: [],
    }
  }

  if (offer.lowestPricePackage?.roomType.amenities.length) {
    return {
      inclusions: [],
      tileAmenities: offer.lowestPricePackage.roomType.amenities,
    }
  }

  const packageRateInclusions = offer.packages.find((pkg) => pkg.inclusions?.length)?.inclusions?.filter(
    (inclusion) => inclusion.displayContext === 'Rate',
  ) ?? []

  return {
    inclusions: [],
    tileAmenities: packageRateInclusions,
  }
}

export function mapOfferDetailToDrawerHotel(detail: OfferDetailResult): DrawerHotel {
  return {
    id: detail.offerId,
    offerId: detail.offerId,
    offerType: detail.offerType,
    slug: detail.slug,
    name: detail.name,
    description: detail.description,
    location: detail.location,
    images: mapImages(detail.images),
    rating: detail.rating,
    reviewsTotal: detail.reviewsTotal,
    inclusions: mapOfferDetailInclusions(detail),
    amenities: mapOfferDetailAmenities(detail),
    price: detail.price !== undefined ? {
      total: detail.price,
      nights: detail.duration ?? 1,
      currency: detail.currencyCode,
    } : undefined,
    value: detail.value,
    discountPercent: detail.discountPercent,
    geoLocation: detail.geoLocation,
    deeplinkUrl: detail.deeplinkUrl,
    saleUnit: detail.saleUnit,
  }
}
